import { Banner, Button } from "@alpacahq/ui/old";
import { Table, type TableProps } from "@alpacahq/ui/old/datagrid";

import type { EliteStatus } from "~/v2/api/rest/account";
import type { Billing, StripeInvoice } from "~/v2/api/rest/billing";
import { Page } from "~/v2/components";
import { getPrettyCash, getPrettyDate } from "~/v2/utils/formatting";
import { PaymentMethodToggle } from "../subscribe/review/presentation/PaymentMethodToggle";
import type { MarketDataPlans } from "../util";
import type { OverviewControllerReturn } from "./Overview.controller";
import { BenefitsCard } from "./presentation/BenefitsCard";
import { CancelModal } from "./presentation/CancelModal";
import { PlaidCard } from "./presentation/PlaidCard";
import { StripeCard } from "./presentation/StripeCard";

interface OverviewPresProps
  extends Omit<OverviewControllerReturn, "stripe" | "elements"> {
  isElite: boolean;
  eliteStatus: EliteStatus | undefined;
  hasEliteMarketData: boolean;
  billing: Billing;
  plans: MarketDataPlans;
  tableProps: TableProps<StripeInvoice>;
  isUpdateDisabled: boolean;
  onSwitchToEliteFreeSubscription: () => void;
}

export const OverviewPres = ({
  isElite,
  eliteStatus,
  hasEliteMarketData,
  billing,
  plans,
  tableProps,
  isBankAccountEnabled,
  isBankAccountEditable,
  paymentMethod,
  onPaymentMethodChange,
  onUpdatePaymentMethodClicked,
  isUpdateLoading,
  isUpdateDisabled,
  plaidStripeLink,
  isEditingStripeCC,
  onEditStripeCCClicked,
  stripeCCError,
  onStripeCcChange,
  // Cancel modal props
  isCancelModalOpen,
  onOpenCancelModal,
  onCloseCancelModal,
  cancelStep,
  cancelReason,
  onCancelReasonChange,
  cancelReasonText,
  onCancelReasonTextChange,
  onCancelModalNextStep,
  onCancelModalPreviousStep,
  onConfirmSubscriptionCancel,
  isCancelingSubscription,
  onSubscribeClicked,
  onSwitchToEliteFreeSubscription,
}: OverviewPresProps) => {
  return (
    <Page title="Market Data Subscription">
      {billing.plan && isElite && (
        <Banner
          variant="info"
          title="Switch to Free Elite Subscription"
          message="You are eligible for a free subscription to Algo Trader Plus through your Elite 100k status."
          button={
            <Button
              variant="v2/filled"
              size="v2/base"
              onClick={onSwitchToEliteFreeSubscription}
            >
              Switch to Free Elite Subscription
            </Button>
          }
        />
      )}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[auto_400px] lg:grid-rows-[auto_auto]">
        {billing.plan || hasEliteMarketData ? (
          <section className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-3 lg:col-start-1 lg:col-end-2 lg:row-start-1 lg:row-end-2">
            <div className="flex items-center justify-between">
              <h2 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
                My Plan
              </h2>
              {billing.plan && (
                <Button
                  variant="v2/ghost"
                  size="v2/base"
                  onClick={onOpenCancelModal}
                  disabled={isCancelingSubscription}
                >
                  Cancel Subscription
                </Button>
              )}
            </div>

            <BenefitsCard
              isElite={hasEliteMarketData}
              isYearly={billing?.is_yearly}
              plans={plans}
              isBeingBilled={!!billing.plan}
            />

            <p className="m-0 flex items-center justify-between gap-2 p-0 text-gray-700 text-sm">
              <span className="font-semibold">
                Algo Trader Plus{" "}
                {!billing.plan && (
                  <>({billing?.is_yearly ? "Annually" : "Monthly"})</>
                )}
              </span>
              <span className="font-mono">
                {!billing.plan
                  ? "Free"
                  : getPrettyCash(
                      billing.is_yearly
                        ? plans.yearly.price
                        : plans.monthly.price,
                      { acceptZero: true, minimumFractionDigits: 2 },
                    )}
              </span>
            </p>
            {billing.plan && (
              <p className="m-0 flex items-center justify-between gap-2 p-0 text-gray-700 text-sm">
                <span className="font-semibold">Next Billing Date</span>
                <span className="font-mono">
                  {billing.nextPaymentDate
                    ? getPrettyDate(
                        new Date(billing?.nextPaymentDate),
                        "MM/DD/YYYY",
                      )
                    : "-"}
                </span>
              </p>
            )}
          </section>
        ) : (
          <section className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-3 lg:col-start-1 lg:col-end-2 lg:row-start-1 lg:row-end-2">
            <p className="m-0 p-0 text-gray-700 text-sm">
              You don't have an active subscription to Algo Trader Plus.
            </p>
            <Button
              variant="v2/filled"
              size="v2/base"
              className="w-fit"
              onClick={onSubscribeClicked}
            >
              Subscribe to Algo Trader Plus
            </Button>
          </section>
        )}
        {billing.plan && (
          <section className="flex flex-col gap-4 lg:col-start-1 lg:col-end-2 lg:row-start-2 lg:row-end-3">
            <h2 className="m-0 p-0 text-left font-semibold text-gray-800 text-lg">
              Invoices
            </h2>
            <Table {...tableProps} />
          </section>
        )}
        {billing.plan && (
          <section className="flex h-fit flex-col gap-4 rounded-lg border border-gray-300 bg-white p-3 lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:row-end-3">
            <h2 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
              Payment Method
            </h2>
            <PaymentMethodToggle
              paymentMethod={paymentMethod}
              onPaymentMethodChange={onPaymentMethodChange}
              isBankAccountEnabled={isBankAccountEnabled}
            />
            {paymentMethod === "stripe" && (
              <StripeCard
                isEditing={isEditingStripeCC}
                onEditClicked={onEditStripeCCClicked}
                onChange={onStripeCcChange}
                errorMessage={stripeCCError}
                card={billing.cards?.[0]}
                isUpdateLoading={isUpdateLoading}
                isUpdateDisabled={isUpdateDisabled}
                onUpdateClicked={onUpdatePaymentMethodClicked}
              />
            )}
            {paymentMethod === "plaid" && (
              <PlaidCard
                plaidAccount={plaidStripeLink.account}
                bankAccount={billing.bank_accounts?.[0]}
                onLinkBankAccount={plaidStripeLink.onOpen}
                isLinkButtonDisabled={!plaidStripeLink.isReady}
                isLinkButtonLoading={plaidStripeLink.isLoading}
                onUpdatePaymentMethodClicked={onUpdatePaymentMethodClicked}
                isUpdatePaymentMethodDisabled={isUpdateDisabled}
                isUpdatePaymentMethodLoading={isUpdateLoading}
                isEditable={isBankAccountEditable}
              />
            )}
          </section>
        )}
        {!billing.plan && hasEliteMarketData && (
          <section className="flex flex-col gap-4 rounded-lg border border-yellow-600 bg-[linear-gradient(106deg,rgba(255,252,216,0.65)_-38.69%,#FFF_97.09%)] p-4 lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:row-end-2">
            <h2 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
              Elite {eliteStatus ?? ""} Status
            </h2>
            <p className="m-0 p-0 text-base text-gray-600">
              You currently have a free subscription to Algo Trader Plus through
              your {eliteStatus ?? ""} Elite status.
            </p>
          </section>
        )}
      </div>
      <CancelModal
        isOpen={isCancelModalOpen}
        onClose={onCloseCancelModal}
        onConfirm={onConfirmSubscriptionCancel}
        step={cancelStep}
        selectedReason={cancelReason}
        onReasonChange={onCancelReasonChange}
        reasonText={cancelReasonText}
        onReasonTextChange={onCancelReasonTextChange}
        onContinueToStep2={onCancelModalNextStep}
        onBackToStep1={onCancelModalPreviousStep}
        isCanceling={isCancelingSubscription}
      />
    </Page>
  );
};
